import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Building2, ChevronDown, ChevronRight, Lock, Search, FileText, FolderOpen, Folder, Loader2, Download } from "lucide-react";
import { api, downloadMultiZip } from "@/lib/api";
import { formatIndianDate } from "@/lib/date";
import { toast } from "sonner";

export default function Vault() {
  const [tree, setTree] = useState([]);
  const [totals, setTotals] = useState({ companies: 0, resolutions: 0 });
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState({});  // {companyId: true, "companyId-fy": true, "companyId-fy-bucket": true}
  const [downloading, setDownloading] = useState({});  // {scopeKey: true}

  useEffect(() => {
    setLoading(true);
    api.get("/vault/tree").then((r) => {
      setTree(r.data?.tree || []);
      setTotals(r.data?.totals || { companies: 0, resolutions: 0 });
    }).finally(() => setLoading(false));
  }, []);  // load once on mount; api/setters are stable refs

  // Client-side filter (already filtered by backend if used q, but keeps search responsive)
  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return tree;
    return tree.filter((c) => c.name.toLowerCase().includes(needle) || (c.cin || "").toLowerCase().includes(needle));
  }, [tree, query]);

  const toggle = (key) => setExpanded((s) => ({ ...s, [key]: !s[key] }));

  const collectFyIds = (fy) =>
    (fy.buckets || []).flatMap((b) => (b.resolutions || []).map((r) => r.id));
  const collectCompanyIds = (co) =>
    (co.fy_nodes || []).flatMap(collectFyIds);

  const handleDownload = async (scopeKey, ids, label) => {
    if (!ids.length) {
      toast.warning("Nothing to download yet in this scope.");
      return;
    }
    setDownloading((s) => ({ ...s, [scopeKey]: true }));
    try {
      await downloadMultiZip(ids, { includeNotice: true, includeMinutes: true, formats: ["pdf", "docx"] });
      toast.success(`Downloaded ${ids.length} resolution${ids.length > 1 ? "s" : ""} from ${label}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "ZIP download failed");
    } finally {
      setDownloading((s) => ({ ...s, [scopeKey]: false }));
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-8" data-testid="vault-page">
      <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="label-eyebrow text-[11px] tracking-[0.2em] text-zinc-500 mb-1">RESOLUTION VAULT</div>
          <h1 className="font-heading text-3xl tracking-tight font-light">All resolutions, organised</h1>
          <p className="text-sm text-zinc-600 mt-1">
            {totals.companies} companies · {totals.resolutions} resolutions · grouped by financial year and meeting type.
          </p>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by company name or CIN…"
          className="w-full pl-10 pr-3 py-2.5 border border-zinc-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:ring-offset-1"
          data-testid="vault-search"
        />
      </div>

      {loading ? (
        <div className="text-center py-16 text-zinc-500 flex items-center justify-center gap-2">
          <Loader2 size={14} className="animate-spin" /> Loading vault…
        </div>
      ) : filtered.length === 0 ? (
        <div className="border border-dashed border-zinc-300 p-12 text-center text-sm text-zinc-500" data-testid="vault-empty">
          {query ? "No companies match your search." : "No companies yet. Add one to start filing resolutions."}
        </div>
      ) : (
        <div className="space-y-3" data-testid="vault-tree">
          {filtered.map((co) => {
            const coOpen = expanded[co.id];
            return (
              <div key={co.id} className="border border-zinc-200 bg-white" data-testid={`vault-company-${co.id}`}>
                <div className="w-full flex items-center hover:bg-zinc-50">
                  <button
                    onClick={() => toggle(co.id)}
                    className="flex-1 px-5 py-4 flex items-center gap-3 text-left"
                    data-testid={`vault-company-toggle-${co.id}`}
                  >
                    {coOpen ? <ChevronDown size={14} className="text-zinc-400" /> : <ChevronRight size={14} className="text-zinc-400" />}
                    <Building2 size={16} className="text-[#002FA7]" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-zinc-900 truncate">{co.name}</div>
                      <div className="text-[11px] text-zinc-500">
                        {co.cin || co.company_type}
                        {co.count > 0 && <span className="ml-3">· {co.count} resolution{co.count === 1 ? "" : "s"}</span>}
                      </div>
                    </div>
                    {co.count === 0 && <span className="text-[10px] uppercase tracking-wider text-zinc-400">Empty</span>}
                  </button>
                  {co.count > 0 && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownload(`co-${co.id}`, collectCompanyIds(co), co.name);
                      }}
                      disabled={!!downloading[`co-${co.id}`]}
                      className="mr-4 inline-flex items-center gap-1.5 text-[11px] tracking-wider uppercase px-3 py-1.5 border border-zinc-300 text-zinc-700 hover:border-[#002FA7] hover:text-[#002FA7] disabled:opacity-40 disabled:cursor-wait"
                      data-testid={`vault-download-company-${co.id}`}
                      title={`Download all ${co.count} resolutions for ${co.name} as ZIP (PDF + DOCX)`}
                    >
                      {downloading[`co-${co.id}`] ? <Loader2 size={11} className="animate-spin" /> : <Download size={11} />}
                      All
                    </button>
                  )}
                </div>
                {coOpen && co.fy_nodes.length === 0 && (
                  <div className="px-5 py-4 border-t border-zinc-100 text-xs text-zinc-500 italic">
                    No resolutions filed yet. <Link to="/generate" className="text-[#002FA7] hover:underline">Generate one →</Link>
                  </div>
                )}
                {coOpen && co.fy_nodes.map((fy) => {
                  const fyKey = `${co.id}-${fy.fy}`;
                  const fyOpen = expanded[fyKey];
                  return (
                    <div key={fy.fy} className="border-t border-zinc-100" data-testid={`vault-fy-${co.id}-${fy.fy}`}>
                      <div className="w-full flex items-center hover:bg-zinc-50">
                        <button
                          onClick={() => toggle(fyKey)}
                          className="flex-1 px-5 py-3 pl-12 flex items-center gap-3 text-left"
                          data-testid={`vault-fy-toggle-${co.id}-${fy.fy}`}
                        >
                          {fyOpen ? <ChevronDown size={12} className="text-zinc-400" /> : <ChevronRight size={12} className="text-zinc-400" />}
                          <Folder size={13} className="text-amber-600" />
                          <span className="text-sm text-zinc-800">FY {fy.fy}</span>
                          <span className="text-[10px] text-zinc-500 ml-auto">{fy.count}</span>
                        </button>
                        {fy.count > 0 && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDownload(`fy-${co.id}-${fy.fy}`, collectFyIds(fy), `${co.name} · FY ${fy.fy}`);
                            }}
                            disabled={!!downloading[`fy-${co.id}-${fy.fy}`]}
                            className="mr-4 inline-flex items-center gap-1.5 text-[11px] tracking-wider uppercase px-3 py-1.5 border border-zinc-300 text-zinc-700 hover:border-[#002FA7] hover:text-[#002FA7] disabled:opacity-40 disabled:cursor-wait"
                            data-testid={`vault-download-fy-${co.id}-${fy.fy}`}
                            title={`Download all ${fy.count} resolutions of FY ${fy.fy} as ZIP (PDF + DOCX)`}
                          >
                            {downloading[`fy-${co.id}-${fy.fy}`] ? <Loader2 size={11} className="animate-spin" /> : <Download size={11} />}
                            FY ZIP
                          </button>
                        )}
                      </div>
                      {fyOpen && fy.buckets.map((b) => {
                        const bKey = `${fyKey}-${b.name}`;
                        const bOpen = expanded[bKey];
                        const bucketIds = (b.resolutions || []).map((r) => r.id);
                        return (
                          <div key={b.name} className="border-t border-zinc-100" data-testid={`vault-bucket-${co.id}-${fy.fy}-${b.name}`}>
                            <div className="w-full flex items-center hover:bg-zinc-50">
                              <button
                                onClick={() => toggle(bKey)}
                                className="flex-1 px-5 py-2.5 pl-20 flex items-center gap-3 text-left"
                              >
                                {bOpen ? <ChevronDown size={11} className="text-zinc-400" /> : <ChevronRight size={11} className="text-zinc-400" />}
                                <FolderOpen size={12} className="text-zinc-500" />
                                <span className="text-[13px] text-zinc-700">{b.name}</span>
                                <span className="text-[10px] text-zinc-500 ml-auto">{b.count}</span>
                              </button>
                              {b.count > 0 && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDownload(
                                      `bucket-${co.id}-${fy.fy}-${b.name}`,
                                      bucketIds,
                                      `${co.name} · FY ${fy.fy} · ${b.name}`,
                                    );
                                  }}
                                  disabled={!!downloading[`bucket-${co.id}-${fy.fy}-${b.name}`]}
                                  className="mr-4 inline-flex items-center gap-1.5 text-[10px] tracking-wider uppercase px-2.5 py-1 border border-zinc-300 text-zinc-700 hover:border-[#002FA7] hover:text-[#002FA7] disabled:opacity-40 disabled:cursor-wait"
                                  data-testid={`vault-download-bucket-${co.id}-${fy.fy}-${b.name}`}
                                  title={`Download all ${b.count} ${b.name} resolutions as ZIP`}
                                >
                                  {downloading[`bucket-${co.id}-${fy.fy}-${b.name}`] ? <Loader2 size={10} className="animate-spin" /> : <Download size={10} />}
                                  ZIP
                                </button>
                              )}
                            </div>
                            {bOpen && (
                              <ul className="border-t border-zinc-100">
                                {b.resolutions.map((r) => (
                                  <li key={r.id} className="border-b border-zinc-50 last:border-0">
                                    <Link
                                      to={`/resolutions/${r.id}`}
                                      className="flex items-center gap-3 py-2 px-5 pl-28 hover:bg-zinc-50 text-[13px]"
                                      data-testid={`vault-resolution-${r.id}`}
                                    >
                                      <FileText size={11} className="text-zinc-400 shrink-0" />
                                      <span className="font-mono-plex text-[11px] text-zinc-500 shrink-0">{r.resolution_number}</span>
                                      <span className="flex-1 truncate text-zinc-800">{r.resolution_title}</span>
                                      <span className="text-[10px] text-zinc-500 shrink-0">{formatIndianDate(r.meeting_date)}</span>
                                      {r.is_special_resolution && (
                                        <span className="text-[9px] tracking-wider px-1.5 py-0.5 bg-[#002FA7] text-white shrink-0">SPECIAL</span>
                                      )}
                                      {r.is_locked && (
                                        <Lock size={11} className="text-amber-700 shrink-0" />
                                      )}
                                    </Link>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
